import { Subscribable, SubscribableEvent, SubscriberId } from '@benzinga/subscribable';
import { LoggingEnvironment } from './environment';
import { Log, LogTarget, Verbosity } from './entities';
import { Session } from '../../session';
import { SafeError } from '@benzinga/safe-await';

interface LoggingVerbosityChanged {
  verbosity: Verbosity;
  type: 'utils-logging:verbosity_changed';
}
interface LoggingLogMessage {
  verbosity: Verbosity;
  log: Log;
  target: LogTarget[];
  type: 'utils-logging:log_message';
}

export type LoggingManagerEvent = LoggingVerbosityChanged | LoggingLogMessage;

export class LoggingManager extends Subscribable<LoggingManagerEvent> {
  private verbosity: Verbosity = 'info';

  constructor(session: Session) {
    super();
    this.verbosity = session.getEnvironment(LoggingEnvironment).verbosity;
    Subscribable.setDebugLoggerCallback(
      (
        eventType: 'dispatch' | 'unsubscribe' | 'subscribe',
        subscribableName: string,
        eventOrId: SubscribableEvent<string> | SubscriberId,
      ) => {
        if (this.verbosity === 'debug') {
          this.log(this.verbosity, { category: eventType, data: eventOrId, message: subscribableName }, ['console']);
        } else if (eventType === 'dispatch' && (eventOrId as SubscribableEvent<string>).type === 'error') {
          const event = eventOrId as {
            error?: SafeError;
            errorType?: string;
            type: 'error';
          };
          this.log(
            'error',
            {
              category: event.errorType ?? '',
              data: event?.error,
              message: event?.error?.toString() ?? '',
            },
            ['console'],
          );
        }
      },
    );
  }

  public static getName = () => 'benzinga-utils-logging';

  public getVerbosity = (): Verbosity => this.verbosity;
  public setVerbosity = (verbosity: Verbosity) => {
    this.verbosity = verbosity;
    this.dispatch({
      type: 'utils-logging:verbosity_changed',
      verbosity,
    });
  };

  public log(verbosity: Verbosity, log: Log, target: LogTarget[] = ['console']): void {
    this.dispatch({
      log,
      target,
      type: 'utils-logging:log_message',
      verbosity,
    });
  }
}
